import { forwardRef, memo, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { Card } from '../base/Card';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti Services
 */
export type ServicesVariant = 'grid' | 'cards' | 'alternating' | 'timeline' | 'minimal';

/**
 * Single Service Item
 */
export interface ServiceItem {
  title: string;
  description: string;
  icon?: React.ReactNode;
  image?: string;
}

export interface ServicesProps extends HTMLAttributes<HTMLElement>, BaseComponentProps {
  /**
   * Variante services
   * @default 'grid'
   */
  variant?: ServicesVariant;
  /**
   * Titolo sezione
   */
  title?: string;
  /**
   * Sottotitolo sezione
   */
  subtitle?: string;
  /**
   * Array di servizi
   */
  services: ServiceItem[];
  /**
   * Colonne per grid variant
   */
  columns?: 2 | 3 | 4;
}

/**
 * Services Component
 * 
 * Sezione Services con 5 varianti diverse.
 * Include ServiceCard per display singolo servizio.
 * Ottimizzato con React.memo per performance.
 * 
 * @example
 * ```tsx
 * <Services
 *   variant="grid"
 *   title="I Nostri Servizi"
 *   services={[
 *     { title: "Web Design", description: "...", icon: <Icon /> },
 *     { title: "Development", description: "..." }
 *   ]}
 * />
 * ```
 */
const ServicesComponent = forwardRef<HTMLElement, ServicesProps>(
  (
    {
      className,
      variant = 'grid',
      title,
      subtitle,
      services,
      columns = 3,
      ...props
    },
    ref
  ) => {
    const gridCols = {
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-2 lg:grid-cols-3',
      4: 'md:grid-cols-2 lg:grid-cols-4',
    };

    // Variant: Grid
    if (variant === 'grid') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-100', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {/* Header */}
            {(title || subtitle) && (
              <div className="text-center mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            {/* Grid */}
            <div className={cn('grid grid-cols-1 gap-8', gridCols[columns])}>
              {services.map((service, index) => (
                <ServiceCard key={index} service={service} variant="icon" />
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Cards
    if (variant === 'cards') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-200', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="text-center mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            <div className={cn('grid grid-cols-1 gap-8', gridCols[columns])}>
              {services.map((service, index) => (
                <ServiceCard key={index} service={service} variant="elevated" />
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Alternating
    if (variant === 'alternating') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-100', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="text-center mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            <div className="space-y-16">
              {services.map((service, index) => (
                <ServiceCard
                  key={index}
                  service={service}
                  variant="alternating"
                  reverse={index % 2 !== 0}
                />
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Timeline
    if (variant === 'timeline') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-200', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="text-center mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            <div className="max-w-4xl mx-auto">
              {services.map((service, index) => (
                <div key={index} className="relative pl-8 pb-12 last:pb-0">
                  {/* Timeline line */}
                  {index < services.length - 1 && (
                    <div className="absolute left-[7px] top-8 bottom-0 w-0.5 bg-primary/30" />
                  )}
                  
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-2 w-4 h-4 rounded-full bg-primary border-4 border-base-200" />
                  
                  <ServiceCard service={service} variant="timeline" />
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Minimal
    if (variant === 'minimal') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-100', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            <div className="max-w-4xl space-y-8">
              {services.map((service, index) => (
                <ServiceCard key={index} service={service} variant="minimal" />
              ))}
            </div>
          </div>
        </section>
      );
    }

    return null;
  }
);

ServicesComponent.displayName = 'Services';

export const Services = memo(ServicesComponent);

/**
 * ServiceCard Component
 * 
 * Card riutilizzabile per singolo servizio.
 * Supporta diverse varianti di display.
 */
interface ServiceCardProps extends BaseComponentProps {
  service: ServiceItem;
  variant: 'icon' | 'elevated' | 'alternating' | 'timeline' | 'minimal';
  reverse?: boolean;
}

const ServiceCardComponent = forwardRef<HTMLDivElement, ServiceCardProps>(
  ({ service, variant, reverse = false, className }, ref) => {
    // Icon variant (per grid)
    if (variant === 'icon') {
      return (
        <div
          ref={ref}
          className={cn(
            'text-center p-8 rounded-xl hover:shadow-lg transition-all duration-300',
            'bg-base-100 border border-base-300',
            'hover:scale-105',
            className
          )}
        >
          {service.icon && (
            <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-primary text-4xl">
              {service.icon}
            </div>
          )}
          <h3 className="text-xl font-bold text-base-content mb-3">
            {service.title}
          </h3>
          <p className="text-base-content/70">
            {service.description}
          </p>
        </div>
      );
    }

    // Elevated variant (card elevate)
    if (variant === 'elevated') {
      return (
        <Card
          ref={ref}
          variant="elevated"
          padding="lg"
          hoverable
          className={cn('group', className)}
        >
          {service.icon && (
            <div className="w-14 h-14 mb-4 flex items-center justify-center text-primary text-3xl group-hover:scale-110 transition-transform">
              {service.icon}
            </div>
          )}
          <h3 className="text-2xl font-bold text-base-content mb-3">
            {service.title}
          </h3>
          <p className="text-base-content/70">
            {service.description}
          </p>
        </Card>
      );
    }

    // Alternating variant
    if (variant === 'alternating') {
      return (
        <div
          ref={ref}
          className={cn(
            'grid md:grid-cols-2 gap-12 items-center',
            reverse && 'md:grid-flow-dense',
            className
          )}
        >
          <div className={cn('space-y-4', reverse && 'md:col-start-2')}>
            {service.icon && (
              <div className="w-12 h-12 flex items-center justify-center text-primary text-3xl">
                {service.icon}
              </div>
            )}
            <h3 className="text-2xl md:text-3xl font-bold text-base-content">
              {service.title}
            </h3>
            <p className="text-lg text-base-content/70">
              {service.description}
            </p>
          </div>
          
          {service.image && (
            <div className={cn('', reverse && 'md:col-start-1 md:row-start-1')}>
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover rounded-xl shadow-lg"
              />
            </div>
          )}
        </div>
      );
    }

    // Timeline variant
    if (variant === 'timeline') {
      return (
        <div ref={ref} className={cn('space-y-2', className)}>
          <h3 className="text-xl font-bold text-base-content">
            {service.title}
          </h3>
          <p className="text-base-content/70">
            {service.description}
          </p>
        </div>
      );
    }

    // Minimal variant
    if (variant === 'minimal') {
      return (
        <div
          ref={ref}
          className={cn(
            'border-l-4 border-primary pl-6 py-2',
            'hover:border-primary-focus transition-colors',
            className
          )}
        >
          <h3 className="text-2xl font-bold text-base-content mb-2">
            {service.title}
          </h3>
          <p className="text-lg text-base-content/70">
            {service.description}
          </p>
        </div>
      );
    }

    return null;
  }
);

ServiceCardComponent.displayName = 'ServiceCard';

export const ServiceCard = memo(ServiceCardComponent);

